import { Translate as i18Translate } from 'next-translate';
import useTranslation from 'next-translate/useTranslation';
import { memo, useMemo } from 'react';

import { Translate } from '@core/constant';
import { useContextCurrency } from '@core/context';
import { useLanguage } from '@core/hook';
import { EIndicator, EProductType } from '@core/type';
import { formatCurrency } from '@core/util';

import { ProductIndicatorProps } from './interface-product';

export const getProductTypeText = (type: EProductType, t: i18Translate): string => {
  switch (type) {
    case EProductType.BAR:
      return t('types.bar');
    case EProductType.COIN:
      return t('types.coin');
    case EProductType.MINTED_BAR:
      return t('types.mintedBar');
    default:
      return t('types.bar');
  }
};

const ProductIndicatorContainer = ({
  indicator: { amount, name },
  productType,
}: ProductIndicatorProps) => {
  const { language } = useLanguage();
  const { currency } = useContextCurrency();
  const { t } = useTranslation(Translate.common.PRODUCT);

  const formattedAmount = formatCurrency(language, currency, amount);

  const indicators = useMemo(
    () => ({
      [EIndicator.COLLECTIBLE]: t('indicators.collectible'),
      [EIndicator.PREMIUM_PER_GRAM]: t('indicators.premiumPerGram', { price: formattedAmount }),
      [EIndicator.PREMIUM_PER_OZ]: t('indicators.premiumPerOz', { price: formattedAmount }),
      [EIndicator.PREMIUM_PER_PRODUCT]: t('indicators.premiumPerProduct', {
        productType: getProductTypeText(productType, t).toLowerCase(),
        price: formattedAmount,
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language, formattedAmount, productType],
  );

  return <>{indicators[name] ?? null}</>;
};

export const ProductIndicator = memo(ProductIndicatorContainer);
